/** @format */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	Component,
	HostListener,
	OnInit,
	PLATFORM_ID,
	Inject,
	Renderer2,
	AfterViewInit
} from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import {
	DeviceService,
	EmitEvent,
	EventBusService,
	Events,
	MyAccountAnalyticsService,
	Store,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';

import { CertonaService } from '@product-recommendations/data-access';
import { AuthenticationService } from '@shared/authentication/data-access';
import { BatfittingService } from '@my-account/data-access/services';
import { LoyaltyBalance } from '@scorecard/data-access';

import { MyAccountService, PointsService, RewardsService } from '@shared/data-access/my-account';
import { BatfittingSession } from '@my-account/data-access/interfaces';
import { BreakPoint, MyAccountErrorLogKeys } from '@shared/data-access/enums';
import {
	Chain,
	Customer,
	Environment,
	KillSwitches,
	PersonalInformation,
	StoredRewards
} from '@shared/data-access/interfaces';
import { Subscription, of, queueScheduler, scheduled } from 'rxjs';
import { catchError, finalize, first, mergeMap, tap } from 'rxjs/operators';

@Component({
	selector: 'myaccount-myaccount-container',
	templateUrl: './myaccount-container.component.html',
	styleUrl: './myaccount-container.component.scss',
	standalone: false
})
export class MyAccountContainerComponent implements OnInit, AfterViewInit {
	public loyaltyBalance: LoyaltyBalance;

	public personalInformation: PersonalInformation;
	public isPublicLands = this.chain.chainIdentifierAbbr === 'pl';
	public isGolfGalaxy = this.chain.chainIdentifierAbbr === 'gg';

	public listsEnabled = false;
	public eventReservationsEnabled = false;

	hitTraxEnabled: boolean;

	batFittingSessions: BatfittingSession[];

	summaryCount: number;
	rewardsCount = 0;
	selectedNavItem: string;

	public custName: string;
	public isMobile = true;
	public cardColor;
	public goldStatus = false;
	public isLoggedIn = false;
	public offerArray$;
	public memberSince;

	public tabs = [
		{ path: 'AccountSummary', label: 'Summary' },
		{ path: 'ScoreCard', label: 'ScoreCard' },
		{ path: 'Orders', label: 'Orders' },
		{ path: 'Favorites', label: 'Favorites' },
		{ path: 'PersonalInformation', label: 'Personal Information' },
		{ path: 'Preferences', label: 'Preferences' },
		{ path: 'Addresses', label: 'Addresses' },
		{ path: 'Settings', label: 'Settings' }
	];

	private singOutAnalyticEvent = {
		event: 'SignOutClick',
		value: true
	};

	get faqsLink(): string {
		return this.chain.chainIdentifierAbbr === 'pl' ? `/s/hub` : `/s/scorecard-faqs`;
	}

	constructor(
		private myAccountAnalyticsService: MyAccountAnalyticsService,
		private authService: AuthenticationService,
		private chain: Chain,
		private eventBusService: EventBusService,
		private environment: Environment,
		private myAccountService: MyAccountService,
		private rewardService: RewardsService,
		private pointsService: PointsService,
		private batfittingService: BatfittingService,
		private store: Store,
		private utilityService: UtilityService,
		private deviceService: DeviceService,
		private route: ActivatedRoute,
		private router: Router,
		private certonaService: CertonaService,
		@Inject(PLATFORM_ID) protected platformId: any,
		@Inject(WINDOW) private window,
		@Inject(DOCUMENT) private document: Document,
		private renderer: Renderer2
	) {
		router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.selectedNavItem = this.router.url;
			}
		});
	}

	ngOnInit(): void {
		this.isLoggedIn = this.route.snapshot.data['authenticated'] || false;

		this.selectedNavItem = this.router.url;
		if (isPlatformBrowser(this.platformId)) {
			// Check the breakpoint
			this.onResize();
		}

		// scheduled killswitches to run before getting myaccount data
		scheduled([this.getKillswitchValues(), this.getMyAccountData()], queueScheduler).subscribe(
			() => {
				if (this.hitTraxEnabled && this.isLoggedIn) {
					this.batfittingService.getBatfittingSessions().subscribe(
						(x) => {
							this.batFittingSessions = x;
						},
						(err) => {
							this.myAccountAnalyticsService.generateLogError(
								MyAccountErrorLogKeys.MYACCOUNT_CONTAINER,
								`Failed to get batfitting sessions.`,
								err
							);
							this.batFittingSessions = [];
						}
					);
				}

				if (this.listsEnabled) {
					const favoritesIndex = this.tabs.findIndex((item) => item.path === 'Favorites');
					if (favoritesIndex > -1) {
						this.tabs[favoritesIndex] = {
							path: 'Lists',
							label: 'Lists'
						};
					}
				}

				if (
					this.eventReservationsEnabled &&
					!this.tabs.some((item) => item.path === 'Appointments')
				) {
					const appointmentsTab = { path: 'Appointments', label: 'Appointments' };
					const listsFavoritesIndex = this.tabs.findIndex(
						(item) => item.path === 'Favorites' || item.path === 'Lists'
					);

					if (listsFavoritesIndex > -1) {
						this.tabs.splice(listsFavoritesIndex + 1, 0, appointmentsTab);
					} else {
						this.tabs.push(appointmentsTab);
					}
				}
			}
		);

		this.setupCertonaRecommendations();
	}

	ngAfterViewInit(): void {
		Promise.resolve().then(() => this.showFooter());
	}

	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		const breakpoint = this.deviceService.getDeviceBreakPoint(true);
		this.isMobile = breakpoint === BreakPoint.EXTRASMALL || breakpoint === BreakPoint.SMALL;
	}

	/**
	 * @description
	 * sends click event to analytics
	 */
	analyticsClickEvent(event): void {
		this.myAccountAnalyticsService.setEventType(event);
		this.myAccountAnalyticsService.triggerAnalyticsEvent(event);
	}

	/**
	 * @description
	 * Logs the current athlete out
	 */
	signOut() {
		// remove favorites, if favorites doesn't exist then this does nothing.
		localStorage.removeItem('favorites');
		// Set analytics Event type
		this.myAccountAnalyticsService.setEventType('SignOutClickEvent');

		// Trigger analytics `SignOutClick` event
		this.myAccountAnalyticsService.triggerAnalyticsEvent(
			'multiUseAnalyticsEvent',
			this.singOutAnalyticEvent
		);

		// Log the athlete out
		return this.authService.logout();
	}

	setErrorAnalytics(message: string): any {
		return () => {
			this.myAccountAnalyticsService.setEventType('SiteError');
			this.myAccountAnalyticsService.setErrorId(message);
			return of(undefined);
		};
	}

	updateCustomer(loyaltyBalance: LoyaltyBalance, personalInformation: PersonalInformation): void {
		const customer: Customer = Object.assign({}, this.store.value['CUSTOMER']);

		customer.x_acNumber = personalInformation?.loyaltyNumber;
		customer.x_acBalancePoints = `${loyaltyBalance?.currentPointBalance || ''}`;

		this.store.set('CUSTOMER', customer);
	}

	private getKillswitchValues(): void {
		this.store
			.select<KillSwitches>('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(first())
			.subscribe((killswitch) => {
				this.hitTraxEnabled = this.utilityService.checkKillSwitch(
					'hitTraxEnabled',
					killswitch,
					this.chain.chainIdentifierAbbr
				);

				this.listsEnabled = this.utilityService.checkKillSwitch(
					'listsEnabled',
					killswitch,
					this.chain.chainIdentifierAbbr
				);
				this.eventReservationsEnabled = this.utilityService.checkKillSwitch(
					'eventReservationsEnabled',
					killswitch,
					this.chain.chainIdentifierAbbr
				);
			});
	}

	/**
	 * @description
	 * gets all of the myaccount data calls and runs them in a forkjoin
	 */
	private getMyAccountData(): Subscription {
		if (!this.isLoggedIn) {
			return of(null).subscribe();
		}

		this.setIsLoading(true);

		return this.myAccountService
			.getPersonalInformationV3()
			.pipe(
				catchError(this.setErrorAnalytics('My Account - Get Personal Information')),
				tap((personalInformation) => {
					this.personalInformation = personalInformation;
					this.custName = personalInformation.firstName
						? `Hi, ${personalInformation.firstName}!`
						: `Hi!`;
					this.memberSince = personalInformation.MemberSince;
					if (!this.isPublicLands && this.personalInformation?.isGold) {
						this.cardColor = 'gold';
						this.goldStatus = true;
					} else {
						this.cardColor = 'green';
					}

					this.eventBusService.emit(
						new EmitEvent(Events.LoyaltyProfileFetched, this.personalInformation)
					);
				}),
				mergeMap(() =>
					this.pointsService.getPointSummary().pipe(
						catchError(this.setErrorAnalytics('My Account - Get Point Summary')),
						tap((loyaltyBalance) => {
							this.loyaltyBalance = loyaltyBalance;
							this.eventBusService.emit(
								new EmitEvent(Events.LoyaltyBalanceFetched, this.loyaltyBalance)
							);
						})
					)
				),
				mergeMap(() =>
					this.rewardService.getRewards(false, true).pipe(
						catchError((err) => {
							this.myAccountAnalyticsService.generateLogError(
								MyAccountErrorLogKeys.MYACCOUNT_CONTAINER,
								'My Account - Get Rewards',
								err
							);

							const errorRewards: StoredRewards = { error: true, rewards: [] };
							return of(errorRewards);
						}),
						tap((rewards) => {
							if (rewards.error === false) {
								this.rewardsCount = rewards.rewards.filter(
									(reward) => reward.active === true
								).length;
							} else {
								this.rewardsCount = 0;
							}
						})
					)
				),
				mergeMap(() => this.getPartyAttributes()),
				finalize(() => {
					this.updateCustomer(this.loyaltyBalance, this.personalInformation);
					this.setIsLoading(false);
				})
			)
			.subscribe();
	}

	shouldShowSpinner() {
		return this.myAccountService.isLoading;
	}

	setIsLoading(value: boolean) {
		this.myAccountService.isLoading = value;
	}

	private getPartyAttributes() {
		if (this.window.party) {
			return this.window.party
				.getPartyAttributes(
					this.environment.apiTokens.party.partnerKey,
					this.environment.apiTokens.party.secretKey
				)
				.then((partyResponse: any) => {
					for (let i = 0; i < partyResponse.attributes.length; i++) {
						if (
							partyResponse.attributes[i].id === 'credit_gold' &&
							partyResponse.attributes[i].value === 'true'
						) {
							this.cardColor = 'black';
							return;
						}
					}
				})
				.catch((err) => {
					this.myAccountAnalyticsService.generateLogError(
						MyAccountErrorLogKeys.MYACCOUNT_PARTY_ATTRIBUTES_ERROR,
						'Failed to get Party attributes',
						err
					);
				});
		} else {
			// fashionably late for party
			window.document.addEventListener('readyToParty', () =>
				this.window.party
					.getPartyAttributes(
						this.environment.apiTokens.party.partnerKey,
						this.environment.apiTokens.party.secretKey
					)
					.then((partyResponse) => {
						for (let i = 0; i < partyResponse.attributes.length; i++) {
							if (
								partyResponse.attributes[i].id === 'credit_gold' &&
								partyResponse.attributes[i].value === 'true'
							) {
								this.cardColor = 'black';
								return;
							}
						}
					})
					.catch((err) => {
						this.myAccountAnalyticsService.generateLogError(
							MyAccountErrorLogKeys.MYACCOUNT_PARTY_ATTRIBUTES_ERROR,
							'Failed to get Party attributes',
							err
						);
						throw err;
					})
			);
		}
	}

	private setupCertonaRecommendations(): void {
		if (typeof this.window.certonaRecommendations === 'undefined') {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.certonaRecommendations = this.getCertonaRecommendations.bind(this);
		}

		// Check to see if a Certona callback has been defined already
		if (
			typeof this.window.DSGShoppingPathJS === 'undefined' ||
			typeof this.window.DSGShoppingPathJS.certonaRecommendations === 'undefined'
		) {
			// Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
			this.window.DSGShoppingPathJS = {
				certonaRecommendations: this.getCertonaRecommendations.bind(this)
			};
		}
	}

	private getCertonaRecommendations(certonaResponse: any): void {
		// Check to see if there is a response from Certona
		if (typeof certonaResponse !== 'undefined') {
			this.certonaService.recommendations(certonaResponse);
		}
	}

	private showFooter(): void {
		const footerId = this.environment.api.reactMarginals.footerHTMLId;
		if (!footerId) {
			return;
		}

		const footerElement = this.document.getElementById(footerId);
		if (footerElement) {
			this.renderer.removeClass(footerElement, 'hideReactFooter');
		}
	}
}
